// View model for the "Stats" tab: the per-day totals bucketed for the time chart, the managers
// split into regular comps and overrides, and the top players.

// The comp statuses in stacking order (bottom first), with their chart colors: the badge
// colors of the issued list (success, info, warning, muted).
export const STATUS_SERIES = [
  { key: 'issued', label: 'Issued', color: '#00BD71' },
  { key: 'pushed', label: 'Pushed', color: '#3861FB' },
  { key: 'pending', label: 'Pending', color: '#FFD22F' },
  { key: 'cancelled', label: 'Cancelled', color: '#7a8499' },
];

// Regular comps vs overrides (issued although a check failed: the Override badge's warning).
export const OVERRIDE_SERIES = [
  { key: 'regular', label: 'Regular', color: '#3861FB' },
  { key: 'override', label: 'Override', color: '#FFD22F' },
];

export const METRICS = [
  { key: 'usd', label: 'Value' },
  { key: 'count', label: 'Count' },
];

// A range longer than this many days is bucketed by week.
export const MAX_DAILY_BUCKETS = 62;

const DAY_MS = 24 * 60 * 60 * 1000;
const dayTime = (day) => Date.parse(`${day}T00:00:00Z`);
const isoDay = (t) => new Date(t).toISOString().slice(0, 10);

// weekStart: the Monday (UTC) of the day's week.
export const weekStart = (day) => {
  const t = dayTime(day);
  const weekday = (new Date(t).getUTCDay() + 6) % 7; // Monday 0 … Sunday 6
  return isoDay(t - weekday * DAY_MS);
};

const emptyBucket = (start) => STATUS_SERIES.reduce(
  (b, s) => ({ ...b, [`${s.key}_count`]: 0, [`${s.key}_usd`]: 0 }),
  { start, count: 0, usd: 0 },
);

// bucketStarts: every bucket from the first to the last day, so quiet days show as gaps.
const bucketStarts = (first, last, weekly) => {
  const step = weekly ? 7 * DAY_MS : DAY_MS;
  const starts = [];
  for (let t = dayTime(weekly ? weekStart(first) : first); t <= dayTime(last); t += step) starts.push(isoDay(t));
  return starts;
};

const roundCents = (n) => Math.round(n * 100) / 100;

// timeBuckets: one row per day (or per week from Monday when the range spans more than
// MAX_DAILY_BUCKETS days) with the count and USD per status ("<status>_count", "<status>_usd")
// and in total. days is the API's per-day-and-status list, oldest first; the range is the
// selected from/to, so quiet days at either end still show (an open end stops at the data).
export const timeBuckets = (days, { from, to } = {}) => {
  if (!days?.length) return { weekly: false, rows: [] };
  const first = from || days[0].day;
  const last = to || days[days.length - 1].day;
  const weekly = (dayTime(last) - dayTime(first)) / DAY_MS + 1 > MAX_DAILY_BUCKETS;
  const keyOf = weekly ? weekStart : (d) => d;
  const sums = days.reduce((acc, d) => {
    const key = keyOf(d.day);
    const b = acc[key] || emptyBucket(key);
    return {
      ...acc,
      [key]: {
        ...b,
        count: b.count + d.count,
        usd: roundCents(b.usd + d.usd),
        [`${d.status}_count`]: (b[`${d.status}_count`] || 0) + d.count,
        [`${d.status}_usd`]: roundCents((b[`${d.status}_usd`] || 0) + d.usd),
      },
    };
  }, {});
  return { weekly, rows: bucketStarts(first, last, weekly).map((s) => sums[s] || emptyBucket(s)) };
};

// bucketLabel: "23 Oct" (or "w/c 20 Oct" for a week), read in UTC.
export const bucketLabel = (start, weekly) => {
  const text = new Date(dayTime(start)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return weekly ? `w/c ${text}` : text;
};

// managerRows: each manager's regular and override comps ("regular_usd", "override_count", …),
// in the API's order (most USD first).
export const managerRows = (managers) => (managers || []).map((m) => ({
  manager: m.manager || '—',
  count: m.count,
  usd: m.usd,
  regular_count: m.count - m.override_count,
  regular_usd: roundCents(m.usd - m.override_usd),
  override_count: m.override_count,
  override_usd: m.override_usd,
}));

// formatUSD: "$9,742.74", as the totals, tooltips and table show it.
export const formatUSD = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatCount = (n) => (n || 0).toLocaleString('en-US');

// formatMetric: the metric's value as the tooltips and table show it.
export const formatMetric = (metric, n) => (metric === 'usd' ? formatUSD(n) : formatCount(n));

// formatTick: an axis tick, in whole dollars ("$12,345") for USD.
export const formatTick = (metric, n) => (metric === 'usd' ? `$${Math.round(n || 0).toLocaleString('en-US')}` : formatCount(n));

// totalLabel: what the totals are: "Given away", or "Total" when cancelled comps are included.
export const totalLabel = (status) => (status === 'all' ? 'Total' : 'Given away');

// share: "12%" of part in whole, "0%" for an empty whole.
export const share = (part, whole) => `${whole > 0 ? Math.round((part / whole) * 100) : 0}%`;

export const playerName = (p) => p.player_email || p.player_nickname || String(p.user_id);
